import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { DataGridPremium, GridRow } from '@mui/x-data-grid-premium';
import { DD_SLASH_MMM_SLASH_YYYY } from 'constants/dateFormat';
import { scGray2 } from 'constants/systemColors';
import moment from 'moment/moment';
import { toBill } from 'services/financial';

import { TABLE_CHECKBOX } from '../../../NewInvoice/InvoiceTable/TableConstants';
import { Amount } from '../../../shared/amount-view/Amount/Amount';
import { DefaultFinanceCell } from '../../../shared/FinanseTableMUI/DefaultFinanceCell';
import { eventNumberComparator, resolveEventSuffix } from '../../SitePaymentServices';
import { SiteApprovalStatus } from '../SitePaymentsConstants';
import { PaymentStatusIcon, SiteApprovalStatusIcon, StatusCell } from '../StatusCell/StatusCell';

import { SiteCreditApplicationContext } from './SiteCreditApplicationContext';

export const SiteCreditApplicationTable = () => {
  const {
    selectedEventForApply,
    rowSelectionModel,
    setRowSelectionModel,
    calculateSelectedAmount,
    creditToApply,
    eligibleEventsForApplyCredit
  } = useContext(SiteCreditApplicationContext);

  const [pinnedColumns, setPinnedColumns] = useState({ left: [TABLE_CHECKBOX] });

  const handlePinnedColumnsChange = useCallback(updatedPinnedColumns => {
    setPinnedColumns({
      ...updatedPinnedColumns,
      left: [TABLE_CHECKBOX, ...updatedPinnedColumns.left.filter(column => column !== TABLE_CHECKBOX)]
    });
  }, []);

  const getTogglableColumns = useCallback(
    columns => columns.filter(column => ![TABLE_CHECKBOX].includes(column.field)).map(column => column.field),
    []
  );

  const columns = useMemo(
    () => [
      {
        field: 'studyName',
        headerName: 'Study',
        flex: 1,
        getApplyQuickFilterFn: undefined,
        minWidth: 110,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'pcn',
        headerName: 'PCN',
        flex: 1,
        getApplyQuickFilterFn: undefined,
        minWidth: 100,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'siteName',
        headerName: 'Site',
        flex: 1,
        getApplyQuickFilterFn: undefined,
        minWidth: 100,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'eventNumber',
        headerName: 'Event ID',
        flex: 1,
        minWidth: 125,
        sortComparator: eventNumberComparator,
        valueGetter: (value, row) => {
          if (row.adjustmentSequence === 0) {
            return resolveEventSuffix(row.eventNumber, row.sitePaymentType);
          }
          return resolveEventSuffix(`${row.eventNumber}.${row.adjustmentSequence}`, row.sitePaymentType);
        }
      },
      {
        field: 'eventName',
        headerName: 'Event Name',
        flex: 1,
        minWidth: 170,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'invoicePaymentStatus',
        headerName: 'Invoice Payment Status',
        flex: 1,
        getApplyQuickFilterFn: undefined,
        minWidth: 170,
        renderCell: ({ value }) => (
          <StatusCell value={value} statusWidth="80px">
            <PaymentStatusIcon status={value} />
          </StatusCell>
        )
      },
      {
        field: 'siteApprovalStatus',
        headerName: 'Site Approval Status',
        flex: 1,
        getApplyQuickFilterFn: undefined,
        minWidth: 170,
        valueGetter: (value, row) => SiteApprovalStatus[row.siteApprovalStatus],
        renderCell: ({ value }) => (
          <StatusCell value={value} statusWidth="140px">
            <SiteApprovalStatusIcon status={value} />
          </StatusCell>
        )
      },
      {
        field: 'siteAmount',
        headerName: 'Site Amount',
        flex: 1,
        minWidth: 150,
        type: 'number',
        valueGetter: (value, row) => toBill(row.siteAmount),
        renderCell: ({ row }) => <Amount coinsAmount={row.siteAmount} showDollarSign />
      },
      {
        field: 'sitePaidAmount',
        headerName: 'Site Paid Amount',
        flex: 1,
        getApplyQuickFilterFn: undefined,
        minWidth: 180,
        type: 'number',
        valueGetter: (value, row) => toBill(row.sitePaidAmount),
        renderCell: ({ row }) => <Amount coinsAmount={row.sitePaidAmount} showDollarSign />
      },
      {
        field: 'sitePaidVariance',
        headerName: 'Site Paid Variance',
        flex: 1,
        minWidth: 170,
        getApplyQuickFilterFn: undefined,
        type: 'number',
        valueGetter: (value, row) => {
          return `${toBill(row.sitePaidAmount - row.siteAmount)}`;
        },
        renderCell: ({ row }) => {
          return <Amount coinsAmount={row.sitePaidAmount - row.siteAmount} showDollarSign />;
        }
      },
      {
        field: 'billNumber',
        headerName: 'Bill #',
        flex: 1,
        minWidth: 105,
        valueGetter: value => value,
        renderCell: ({ row: { billNumber } }) => <div>{billNumber || '—'}</div>
      },
      {
        field: 'revenueDate',
        headerName: 'Revenue Date',
        flex: 1,
        getApplyQuickFilterFn: undefined,
        minWidth: 160,
        type: 'date',
        valueGetter: value => value && new Date(value),
        renderCell: ({ row: { revenueDate } }) => (
          <div>{revenueDate ? moment(revenueDate).format(DD_SLASH_MMM_SLASH_YYYY) : '—'}</div>
        )
      },
      {
        field: 'invoiceDate',
        headerName: 'Invoice Date',
        flex: 1,
        getApplyQuickFilterFn: undefined,
        minWidth: 160,
        type: 'date',
        valueGetter: value => value && new Date(value),
        renderCell: ({ row: { invoiceDate } }) => (
          <div>{invoiceDate ? moment(invoiceDate).format(DD_SLASH_MMM_SLASH_YYYY) : '—'}</div>
        )
      },
      {
        field: 'invoiceNumber',
        headerName: 'Invoice Number',
        flex: 1,
        getApplyQuickFilterFn: undefined,
        minWidth: 175,
        sortComparator: eventNumberComparator,
        valueGetter: (value, row) =>
          row.invoiceNumber ? resolveEventSuffix(row.invoiceNumber, row.sitePaymentType) : '',
        renderCell: DefaultFinanceCell
      }
    ],
    []
  );

  const handleRowSelectionModelChange = useCallback(
    newRowSelectionModel => {
      const selectedIds = Array.from(newRowSelectionModel.ids);
      const selectedAmount = calculateSelectedAmount(selectedIds, eligibleEventsForApplyCredit);
      const isAddingRow = selectedEventForApply.length + 1 === selectedIds.length;
      const isRemovingRow = selectedIds.length < selectedEventForApply.length;
      const creditBalance = creditToApply - selectedAmount;

      if ((isAddingRow && creditBalance < 0) || creditBalance >= 0 || isRemovingRow) {
        setRowSelectionModel(newRowSelectionModel);
      }
    },
    [
      calculateSelectedAmount,
      creditToApply,
      eligibleEventsForApplyCredit,
      selectedEventForApply.length,
      setRowSelectionModel
    ]
  );

  const tableData = useMemo(() => {
    const selectedAmount = calculateSelectedAmount(selectedEventForApply, eligibleEventsForApplyCredit);
    const creditBalance = creditToApply - selectedAmount;
    return eligibleEventsForApplyCredit.map(item => {
      return {
        ...item,
        disabled: creditBalance <= 0 && !selectedEventForApply.includes(item.itemSiteId)
      };
    });
  }, [calculateSelectedAmount, creditToApply, eligibleEventsForApplyCredit, selectedEventForApply]);

  const isRowSelectable = useCallback(({ row }) => {
    return !row.disabled;
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '600px',
        flexDirection: 'column',
        padding: '0 0 0 0'
      }}
    >
      <DataGridPremium
        getRowId={row => row.itemSiteId}
        rows={tableData}
        columns={columns}
        rowHeight={38}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        rowSelectionModel={rowSelectionModel}
        disableRowSelectionExcludeModel
        isRowSelectable={isRowSelectable}
        initialState={{
          columns: {
            columnVisibilityModel: { revenueDate: false, invoiceDate: false, invoiceNumber: false }
          },
          pinnedColumns
        }}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        slots={{
          row: CustomRow
        }}
        slotProps={{
          columnsManagement: {
            getTogglableColumns
          },
          toolbar: {
            printOptions: { disableToolbarButton: true },
            excelOptions: { disableToolbarButton: true },
            csvOptions: { disableToolbarButton: true }
          }
        }}
        sx={{
          padding: '20px 40px 0 40px',
          backgroundColor: scGray2,
          border: 'none',
          '.MuiDataGrid-pinnedColumns, .MuiDataGrid-pinnedColumnHeaders': {
            backgroundColor: scGray2
          }
        }}
        checkboxSelection
        disableRowSelectionOnClick
        disableRowGrouping
        disableAggregation
        disablePivoting
        showToolbar
      />
    </Box>
  );
};

const CustomRow = props => {
  return <GridRow {...props} data-testid="table-row" />;
};
